import { Injectable, NgZone } from '@angular/core';
import { LoggerService } from './logger.service';
import { WebSocketMessageType } from 'src/app/core/services/user.service';
import { environment } from '@env/environment';
import { logErrorWithTime } from 'src/app/shared/utils/date-utils';
import { logInfoWithTime } from 'src/app/shared/utils/date-utils';

export const wsErrorMessage: Record<number, string> = {
  1000: 'Normal closure',
  1001: 'Endpoint is going away',
  1002: 'Protocol error',
  1003: 'Unsupported data',
  1006: 'Abnormal closure (connection lost)', // sometimes not provided by browser
  1008: 'Policy violation',
  1009: 'Message too big',
  1011: 'Server error',
  4000: 'Application-specific error (server)',
};

interface SubscriptionMeta {
  subscriptionType: WebSocketMessageType;
  subscriptionKey: string;
  id: any;
  callback: (id: any, data: any) => void;
  logPrefix: string;
  socketKey: string;
}

interface SocketMessageOptions<T> {
  event: MessageEvent;
  id: T;
  callback: (id: T, data: any) => void;
  logPrefix: string;
  socketKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private static readonly ATTEMPTS_BEFORE_LOG_ESCALATION = 5;
  private static readonly BASE_DELAY_MS = 1000; // 1s, 2s, 4s, 8s, 16s
  private static readonly MAX_DELAY_MS = 30 * 1000; // backoff ceiling; retries never stop
  // Client-side firewalls and nginx drop idle tunnels after 5-10 minutes, so the
  // ping must land well inside the shortest observed cutoff (5 min).
  private static readonly HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
  // A ping without a pong inside this window means the connection is half-open
  // (silently dropped by a middlebox): the browser still reports OPEN and never
  // fires 'close', so this deadline is the only way to detect it.
  private static readonly PONG_TIMEOUT_MS = 10 * 1000;

  // Store sockets by key for multiple connections if needed.
  private sockets: { [key: string]: WebSocket[] } = {};

  // Reconnection state
  private subscriptions: Map<string, SubscriptionMeta> = new Map();
  private intentionallyClosed: WeakSet<WebSocket> = new WeakSet();
  private heartbeatIntervals: Map<string, ReturnType<typeof setInterval>> =
    new Map();
  private reconnectTimeouts: Map<string, ReturnType<typeof setTimeout>> =
    new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private pongDeadlines: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Progress listeners multiplexed over the long-lived user socket, keyed by fingerprint id.
   * The server attaches task and user subscriptions to the same connection, so one socket
   * carries both and progress survives for as long as the app is open.
   */
  private taskListeners: Map<string, (data: any) => void> = new Map();

  public SOCKET_KEY_USER = 'user';

  constructor(
    private logger: LoggerService,
    private zone: NgZone,
  ) {
    // Recover immediately after sleep/wake or a network blip instead of waiting
    // out the heartbeat tick or the backoff timer. Root-provided singleton, so
    // these listeners live for the whole app session and need no teardown.
    //
    // Everything the socket machinery schedules (events, retry timers, pings)
    // stays OUTSIDE the Angular zone: zone-tracked tasks would keep the app
    // permanently unstable while the server is down (there is always a pending
    // retry), which blocked initial navigation and hung the app before login.
    // The zone is re-entered only when a message is delivered to a subscriber.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('online', () =>
        this.checkAllConnections('online'),
      );
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkAllConnections('tab visible');
        }
      });
    });
  }

  /**
   * Creates or retrieves an existing WebSocket connection.
   * @param key - A unique key for the WebSocket connection. Defaults to 'default'.
   * @returns The WebSocket instance.
   */
  private createWebSocket(key: string = 'default'): WebSocket {
    // Outside the Angular zone: zone.js binds event tasks to the zone active at
    // REGISTRATION time, so the socket and its listeners must be created here to
    // keep connection churn from driving change detection / app stability.
    return this.zone.runOutsideAngular(() => this.createWebSocketInternal(key));
  }

  private createWebSocketInternal(key: string): WebSocket {
    if (!this.sockets[key]) this.sockets[key] = [];

    // Build the URL from environment settings (ensure it already includes ws:// or wss://).
    // `hostWS` is separate from `host` because in dev the socket must follow the API's
    // backend, not the local machine.
    const wsUrl =
      environment.schemeWS +
      environment.hostWS +
      environment.portWS +
      environment.pathWS;

    let socket: WebSocket;
    try {
      logInfoWithTime(`WebSocket: ${key} attempting to connect to -> ${wsUrl}`);
      socket = new WebSocket(wsUrl);
    } catch (err) {
      // Only catches constructor issues (bad URL, blocked by CSP, etc.)
      logErrorWithTime(`WebSocket connection failed -> error=[${err}]`);
    }

    this.sockets[key].push(socket);

    // Helpful: log state changes
    socket.addEventListener('open', () => {
      logInfoWithTime(
        `WebSocket: ${key} successfully connected to -> ${wsUrl}`,
      );
    });

    // Close has diagnostics: code + reason
    socket.addEventListener('close', (event: CloseEvent) => {
      const { code, reason, wasClean } = event;
      const message = wsErrorMessage[code] ?? 'Unknown close reason';

      if (!wasClean || code != 1000) {
        logErrorWithTime(
          `WebSocket Connection Error: key=[${key}] closed_clean=[${wasClean}] | code=[${code}] | message=[${message}] | reason=[${reason}]`,
        );
      } else {
        logInfoWithTime(
          `WebSocket Connection Closed: key=[${key}] closed_clean=[${wasClean}] | code=[${code}] | message=[${message}]`,
        );
      }
      // Remove only THIS socket from the list
      const list = this.sockets[key];
      if (list) {
        const idx = list.indexOf(socket);
        if (idx !== -1) list.splice(idx, 1);
        if (list.length === 0) delete this.sockets[key]; // optional: prune empty key
      }

      // A pending pong deadline belongs to the socket that just died; without this
      // it would fire later and close the replacement connection.
      this.clearPongDeadline(key);

      // Trigger reconnect unless THIS socket was closed on purpose.
      // Tracked per socket rather than per key: a key-level flag is never cleared when
      // disconnect() finds nothing to close, and then suppresses a later real reconnect.
      if (!this.intentionallyClosed.has(socket)) {
        logInfoWithTime(
          `WebSocket: ${key} closed unexpectedly, scheduling reconnect`,
        );
        this.stopHeartbeat(key);
        this.scheduleReconnect(key);
      }
    });

    return socket;
  }
  /**
   * Generic method to open a WebSocket connection and subscribe to a specific channel.
   *
   * @param subscriptionType - The message type to send (e.g. MESSAGE_SUBSCRIBE_TASK or MESSAGE_SUBSCRIBE_USER)
   * @param subscriptionKey - The key for the subscription parameter (e.g. 'taskId' or 'userId')
   * @param id - The identifier value for the subscription
   * @param callback - A callback function to call when a message is received
   * @param logPrefix - A string used for logging (e.g. 'Task Progress' or 'User Notification')
   * @param socketKey - Optional key to identify the WebSocket instance. Defaults to 'default'.
   */
  private connect<T>(
    subscriptionType: WebSocketMessageType,
    subscriptionKey: string,
    id: T,
    callback: (id: T, data: any) => void,
    logPrefix: string,
    socketKey: string = 'default',
  ): void {
    // Store subscription metadata for reconnection
    this.subscriptions.set(socketKey, {
      subscriptionType,
      subscriptionKey,
      id,
      callback,
      logPrefix,
      socketKey,
    });

    // Handler registration must also happen outside the zone (connect() is
    // called from zone-bound app code; see the constructor note).
    this.zone.runOutsideAngular(() => {
      const webSocket = this.createWebSocket(socketKey);

      webSocket.onopen = () => {
        const payload: { type: WebSocketMessageType; [key: string]: any } = {
          type: subscriptionType,
        };
        payload[subscriptionKey] = id;
        webSocket.send(JSON.stringify(payload));
        this.logger.debug(
          `${logPrefix} - Connected and subscribed with payload:`,
          payload,
        );
        if (socketKey === this.SOCKET_KEY_USER) {
          this.resubscribeTasks(webSocket);
        }
        this.startHeartbeat(socketKey);
      };

      webSocket.onmessage = (event) =>
        this.handleSocketMessage({ event, id, callback, logPrefix, socketKey });
    });
  }

  /**
   * Forwards an incoming socket message to its subscriber.
   *
   * Keepalive replies are swallowed here so subscribers never see them as payloads —
   * callbacks read `message.notification`, which a pong does not carry. Progress frames are
   * routed to their per-task listener instead of the socket's own subscriber.
   */
  private handleSocketMessage<T>(options: SocketMessageOptions<T>): void {
    const { event, id, callback, logPrefix, socketKey } = options;
    this.logger.debug(`${logPrefix} - Received message:`, event.data);

    try {
      const data = JSON.parse(event.data);
      if (data?.type === WebSocketMessageType.MESSAGE_PONG) {
        this.logger.debug(`${logPrefix} - Keepalive pong received`);
        this.clearPongDeadline(socketKey);
      } else if (!this.dispatchTaskProgress(data)) {
        // The socket lives outside the Angular zone; re-enter it here so the
        // subscriber's state updates trigger change detection.
        this.zone.run(() => callback(id, data));
      }
    } catch (error) {
      this.logger.error(`${logPrefix} - Error parsing JSON:`, error);
    }
  }

  /**
   * Subscribes to progress updates for one task, multiplexed over the user socket.
   *
   * Ownership is deliberately independent of any component: a subscription tied to a
   * short-lived toast died seconds after it opened, while the job it tracked ran for
   * minutes, so every later progress frame was dropped server-side.
   *
   * @param fingerprintId - The task to track.
   * @param callback - Invoked with each progress frame for that task.
   */
  public subscribeTaskProgress(
    fingerprintId: string,
    callback: (data: any) => void,
  ): void {
    this.taskListeners.set(fingerprintId, callback);

    const socket = this.getOpenSocket(this.SOCKET_KEY_USER);
    if (socket) {
      this.sendTaskSubscription(socket, fingerprintId);
    } else {
      this.logger.debug(
        `Task Progress - user socket not open, ${fingerprintId} will subscribe on connect`,
      );
    }
  }

  /**
   * Stops tracking progress for a task. Call this once the task reports it is finished.
   */
  public unsubscribeTaskProgress(fingerprintId: string): void {
    this.taskListeners.delete(fingerprintId);
    this.logger.debug(`Task Progress - stopped tracking ${fingerprintId}`);
  }

  /**
   * Returns the current socket for a key, but only while it is open.
   */
  private getOpenSocket(socketKey: string): WebSocket | undefined {
    const list = this.sockets[socketKey];
    const socket = list?.[list.length - 1];

    return socket && socket.readyState === WebSocket.OPEN ? socket : undefined;
  }

  private sendTaskSubscription(socket: WebSocket, fingerprintId: string): void {
    socket.send(
      JSON.stringify({
        type: WebSocketMessageType.MESSAGE_SUBSCRIBE_TASK,
        fingerprint_id: fingerprintId,
      }),
    );
    this.logger.debug(`Task Progress - subscribed task ${fingerprintId}`);
  }

  /**
   * Restores every task subscription on a fresh socket.
   *
   * The server holds subscriptions per connection, so without this a reconnect silently
   * stops progress for tasks that are still running.
   */
  private resubscribeTasks(socket: WebSocket): void {
    this.taskListeners.forEach((_callback, fingerprintId) => {
      this.sendTaskSubscription(socket, fingerprintId);
    });
  }

  /**
   * Routes a progress frame to the listener registered for its task.
   *
   * Every progress frame counts as consumed, even with no listener. There is no server-side
   * unsubscribe, so frames keep arriving after a task finishes; letting them fall through to
   * the notification subscriber would raise the unread counter and toast an empty payload.
   *
   * @returns true when the frame was a progress frame and must not be forwarded.
   */
  private dispatchTaskProgress(data: any): boolean {
    if (data?.type !== WebSocketMessageType.MESSAGE_TASK_PROGRESS) {
      return false;
    }

    const listener = this.taskListeners.get(data.fingerprint_id);
    if (listener) {
      // Same zone re-entry as notification callbacks: progress frames update UI.
      this.zone.run(() => listener(data));
    } else {
      this.logger.debug(
        `Task Progress - no listener for ${data.fingerprint_id}, frame ignored`,
      );
    }

    return true;
  }

  /**
   * Connects to the WebSocket server to subscribe for user notifications.
   *
   * @param userId - The ID of the user to subscribe notifications for.
   * @param callback - A callback function that will be called when a notification is received.
   */
  public connectUserNotifications(
    userId: string,
    callback: (id: string, notification: any) => void,
  ): void {
    this.connect(
      WebSocketMessageType.MESSAGE_SUBSCRIBE_USER,
      'user_id',
      userId,
      callback,
      'User Notification',
      this.SOCKET_KEY_USER,
    );
  }

  /**
   * Disconnects a specific WebSocket connection.
   *
   * @param socketKey - The key identifying the WebSocket connection to disconnect. Defaults to 'default'.
   */
  public disconnect(socketKey: string = 'default'): void {
    this.stopHeartbeat(socketKey);
    this.clearPongDeadline(socketKey);
    this.subscriptions.delete(socketKey);
    this.cancelPendingReconnect(socketKey);

    const sockets = this.sockets[socketKey];
    if (sockets) {
      sockets.forEach((socket) => {
        // CONNECTING is included so a socket mid-handshake is not left dangling.
        if (
          socket &&
          (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING)
        ) {
          this.intentionallyClosed.add(socket);
          socket.close(1000, 'Client disconnecting');
          this.logger.debug(`WebSocket (${socketKey}) is closing.`);
        }
      });
    }
  }

  /**
   * Disconnects all active WebSocket connections.
   */
  public disconnectAll(): void {
    Object.keys(this.sockets).forEach((key) => {
      this.disconnect(key);
    });
  }

  // ---- Reconnection logic ----

  private scheduleReconnect(socketKey: string): void {
    const meta = this.subscriptions.get(socketKey);
    if (!meta) {
      logInfoWithTime(
        `WebSocket: ${socketKey} no subscription metadata, skipping reconnect`,
      );
      return;
    }

    const attempt = (this.reconnectAttempts.get(socketKey) ?? 0) + 1;
    this.reconnectAttempts.set(socketKey, attempt);

    // Recovery is deliberately invisible: the socket only carries convenience
    // notifications, retries never stop, and the app works fine without it —
    // so a prolonged outage is logged for troubleshooting, never surfaced as UI.
    if (attempt === SocketService.ATTEMPTS_BEFORE_LOG_ESCALATION) {
      logErrorWithTime(
        `WebSocket: ${socketKey} still down after ${attempt} attempts, retrying in background`,
      );
    }

    const delay = Math.min(
      SocketService.BASE_DELAY_MS * Math.pow(2, attempt - 1),
      SocketService.MAX_DELAY_MS,
    );
    logInfoWithTime(
      `WebSocket: ${socketKey} reconnect attempt ${attempt} in ${delay}ms`,
    );

    const timeout = this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.reconnectTimeouts.delete(socketKey);
        this.attemptReconnect(meta);
      }, delay),
    );

    this.reconnectTimeouts.set(socketKey, timeout);
  }

  private attemptReconnect(meta: SubscriptionMeta): void {
    const {
      subscriptionType,
      subscriptionKey,
      id,
      callback,
      logPrefix,
      socketKey,
    } = meta;

    this.zone.runOutsideAngular(() => {
      const webSocket = this.createWebSocket(socketKey);

      webSocket.onopen = () => {
        const payload: { type: WebSocketMessageType; [key: string]: any } = {
          type: subscriptionType,
        };
        payload[subscriptionKey] = id;
        webSocket.send(JSON.stringify(payload));
        logInfoWithTime(
          `WebSocket: ${socketKey} reconnected and re-subscribed`,
        );

        if (socketKey === this.SOCKET_KEY_USER) {
          this.resubscribeTasks(webSocket);
        }

        // Reset reconnect state
        this.reconnectAttempts.delete(socketKey);

        // Restart heartbeat
        this.startHeartbeat(socketKey);
      };

      webSocket.onmessage = (event) =>
        this.handleSocketMessage({ event, id, callback, logPrefix, socketKey });
    });
  }

  // ---- Heartbeat (idle timeout detection) ----

  private startHeartbeat(socketKey: string): void {
    this.stopHeartbeat(socketKey);

    const interval = this.zone.runOutsideAngular(() =>
      setInterval(() => {
        const socketList = this.sockets[socketKey];
        if (!socketList || socketList.length === 0) {
          logInfoWithTime(
            `WebSocket: ${socketKey} heartbeat found no active socket, reconnecting`,
          );
          this.stopHeartbeat(socketKey);
          this.scheduleReconnect(socketKey);
          return;
        }

        const socket = socketList[socketList.length - 1];
        if (socket.readyState !== WebSocket.OPEN) {
          logInfoWithTime(
            `WebSocket: ${socketKey} heartbeat detected non-OPEN state (${socket.readyState})`,
          );
          this.stopHeartbeat(socketKey);
          if (socket.readyState === WebSocket.CLOSED) {
            this.scheduleReconnect(socketKey);
          }
        } else {
          // Must actually transmit, not just inspect readyState: the proxy closes the tunnel
          // after its read timeout, and only a server reply to this ping resets that timer.
          socket.send(
            JSON.stringify({ type: WebSocketMessageType.MESSAGE_PING }),
          );
          logInfoWithTime(`WebSocket: ${socketKey} heartbeat ping sent`);
          this.armPongDeadline(socketKey, socket);
        }
      }, SocketService.HEARTBEAT_INTERVAL_MS),
    );

    this.heartbeatIntervals.set(socketKey, interval);
  }

  private stopHeartbeat(socketKey: string): void {
    const interval = this.heartbeatIntervals.get(socketKey);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(socketKey);
    }
  }

  // ---- Dead-connection detection (pong deadline) ----

  /**
   * Arms a deadline after a ping goes out: if no pong arrives in time, the
   * connection is half-open (a firewall dropped it without FIN/RST — the
   * browser still reports OPEN and will never fire 'close' on its own).
   * Force-closing the socket converts the silent drop into a normal close,
   * which drives the existing reconnect + resubscribe path.
   */
  private armPongDeadline(socketKey: string, socket: WebSocket): void {
    if (this.pongDeadlines.has(socketKey)) {
      return;
    }

    const timeout = this.zone.runOutsideAngular(() =>
      setTimeout(() => {
        this.pongDeadlines.delete(socketKey);
        logErrorWithTime(
          `WebSocket: ${socketKey} no pong within ${SocketService.PONG_TIMEOUT_MS}ms, closing dead connection`,
        );
        if (
          socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING
        ) {
          // Not marked intentionallyClosed, so the close handler reconnects.
          socket.close();
        } else if (!this.getOpenSocket(socketKey)) {
          // The pinged socket died some other way and nothing replaced it.
          this.stopHeartbeat(socketKey);
          this.scheduleReconnect(socketKey);
        }
      }, SocketService.PONG_TIMEOUT_MS),
    );

    this.pongDeadlines.set(socketKey, timeout);
  }

  private clearPongDeadline(socketKey: string): void {
    const timeout = this.pongDeadlines.get(socketKey);
    if (timeout) {
      clearTimeout(timeout);
      this.pongDeadlines.delete(socketKey);
    }
  }

  // ---- Wake / network-restore recovery ----

  /**
   * Health-checks every registered subscription. Called on 'online' and when
   * the tab becomes visible again: after laptop sleep the socket often looks
   * OPEN but is dead, and a pending backoff timer would otherwise make the
   * user wait out the full delay.
   */
  private checkAllConnections(trigger: string): void {
    this.subscriptions.forEach((meta, socketKey) => {
      logInfoWithTime(`WebSocket: ${socketKey} health check (${trigger})`);

      // A backoff wait is in progress — retry right now instead.
      const pending = this.reconnectTimeouts.get(socketKey);
      if (pending) {
        clearTimeout(pending);
        this.reconnectTimeouts.delete(socketKey);
        this.attemptReconnect(meta);
        return;
      }

      const list = this.sockets[socketKey];
      const latest = list?.[list.length - 1];

      // Handshake or close already in flight — its own events drive what happens next.
      if (
        latest?.readyState === WebSocket.CONNECTING ||
        latest?.readyState === WebSocket.CLOSING
      ) {
        return;
      }

      if (latest?.readyState === WebSocket.OPEN) {
        // Probe: a half-open socket still reports OPEN; only a missing pong reveals it.
        latest.send(
          JSON.stringify({ type: WebSocketMessageType.MESSAGE_PING }),
        );
        this.armPongDeadline(socketKey, latest);
      } else {
        this.stopHeartbeat(socketKey);
        this.scheduleReconnect(socketKey);
      }
    });
  }

  // ---- Cleanup helpers ----

  private cancelPendingReconnect(socketKey: string): void {
    const timeout = this.reconnectTimeouts.get(socketKey);
    if (timeout) {
      clearTimeout(timeout);
      this.reconnectTimeouts.delete(socketKey);
    }
    this.reconnectAttempts.delete(socketKey);
  }
}
